import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight, Mail, MapPin, Phone, Loader2 } from "lucide-react";
import { SERVICES } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", company: "", service: SERVICES[0].title, message: "" };

export const LeadForm = () => {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success("Thanks! Our team will reach out within one business day.");
      setForm(initial);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    "w-full bg-white border border-[#E5E5E5] px-4 py-3.5 text-[#0A0A0A] placeholder:text-[#A3A3A3] outline-none focus:border-[#002DFF] focus:ring-2 focus:ring-[#002DFF]/20 transition-colors";

  return (
    <section id="contact" data-testid="contact-section" className="py-20 md:py-32 bg-[#FAFAFA] border-b border-[#E5E5E5]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 border border-[#0A0A0A] bg-white">
          {/* Left info panel */}
          <div className="lg:col-span-5 bg-[#0A0A0A] text-white p-8 md:p-12 flex flex-col">
            <span className="font-mono-nw text-xs tracking-[0.25em] uppercase text-[#002DFF]">
              Get started
            </span>
            <h2 className="mt-4 font-display font-black uppercase tracking-tighter text-4xl sm:text-5xl leading-[0.95]">
              Scale your
              <br />
              operations today
            </h2>
            <p className="mt-6 text-white/60 leading-relaxed">
              Tell us what you need and we’ll design a managed team around it. No
              obligation, no pressure — just a clear plan and pricing.
            </p>

            <div className="mt-auto pt-10 space-y-5">
              <div className="flex items-center gap-4">
                <Mail className="w-5 h-5 text-[#002DFF]" strokeWidth={1.5} />
                <span className="text-sm">hello@novaworksglobal.com</span>
              </div>
              <div className="flex items-center gap-4">
                <Phone className="w-5 h-5 text-[#002DFF]" strokeWidth={1.5} />
                <span className="text-sm">1 (844) 656-1470</span>
              </div>
              <div className="flex items-center gap-4">
                <MapPin className="w-5 h-5 text-[#002DFF]" strokeWidth={1.5} />
                <span className="text-sm">Knoxville, TN · Davao City, PH · General Santos, PH</span>
              </div>
            </div>
          </div>

          {/* Form */}
          <form
            data-testid="lead-form"
            onSubmit={submit}
            className="lg:col-span-7 p-8 md:p-12 grid grid-cols-1 sm:grid-cols-2 gap-5"
          >
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">Name *</label>
              <input data-testid="lead-name" className={`mt-2 ${inputClass}`} value={form.name} onChange={update("name")} placeholder="Jane Doe" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">Work Email *</label>
              <input data-testid="lead-email" type="email" className={`mt-2 ${inputClass}`} value={form.email} onChange={update("email")} placeholder="jane@company.com" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">Company</label>
              <input data-testid="lead-company" className={`mt-2 ${inputClass}`} value={form.company} onChange={update("company")} placeholder="Company Inc." />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">Service</label>
              <select data-testid="lead-service" className={`mt-2 ${inputClass}`} value={form.service} onChange={update("service")}>
                {SERVICES.map((s) => (
                  <option key={s.id} value={s.title}>{s.title}</option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">How can we help? *</label>
              <textarea data-testid="lead-message" rows={4} className={`mt-2 ${inputClass} resize-none`} value={form.message} onChange={update("message")} placeholder="Tell us about your team, volumes and goals…" />
            </div>
            <div className="sm:col-span-2">
              <p className="text-xs leading-relaxed text-[#525252]" data-testid="lead-legal-ack">
                By submitting this form, you acknowledge our{" "}
                <Link to="/terms" className="text-[#002DFF] font-semibold hover:underline">
                  Terms &amp; Conditions
                </Link>{" "}
                and{" "}
                <Link to="/privacy" className="text-[#002DFF] font-semibold hover:underline">
                  Privacy Policy
                </Link>
                .
              </p>
            </div>
            <div className="sm:col-span-2">
              <button
                type="submit"
                data-testid="lead-submit"
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#0A0A0A] text-white px-7 py-4 text-xs font-bold uppercase tracking-[0.15em] border border-[#0A0A0A] hover:bg-[#002DFF] hover:border-[#002DFF] transition-colors disabled:opacity-60"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>
                ) : (
                  <>Request a Quote <ArrowUpRight className="w-4 h-4" /></>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};
